import { Injectable } from '@kaokei/use-vue-service';

@Injectable()
export class StorageService {
  /**
   * 存储特定比赛被动交卷需要只弹窗一次提示
   */
  private KEY_CONTEST_PASSIVE_SUBMITED = 'KEY_CONTEST_PASSIVE_SUBMITED';
  private getItem(key: string) {
    const data = localStorage.getItem(key) as string;
    try {
      // 转成json数据
      return JSON.parse(data);
    } catch {
      return data;
    }
  }
  private setItem(key: string, value: any) {
    try {
      const data = typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, data);
    } catch {
      // do nothing
    }
  }
  private removeItem(key: string) {
    localStorage.removeItem(key);
  }

  private getItemWithPathname(key: string) {
    const pname = location.pathname;
    return this.getItem(`${key}_${pname}`);
  }
  private setItemWithPathname(key: string, value: any) {
    const pname = location.pathname;
    return this.setItem(`${key}_${pname}`, value);
  }

  /**
   * 存储分页大小
   * 每个页面的分页大小是独立的
   */
  private KEY_PAGE_SIZE = 'KEY_PAGE_SIZE';
  public getPageSize() {
    return this.getItemWithPathname(this.KEY_PAGE_SIZE);
  }
  public setPageSize(value: any) {
    return this.setItemWithPathname(this.KEY_PAGE_SIZE, value);
  }

  /**
   * 存储用户选择的域
   */
  private KEY_ZONE = 'KEY_ZONE';
  public getZone() {
    return this.getItem(this.KEY_ZONE);
  }
  public setZone(value: any) {
    return this.setItem(this.KEY_ZONE, value);
  }
  public removeZone() {
    return this.removeItem(this.KEY_ZONE);
  }

  /**
   * 存储是否展示域引导
   */
  private KEY_ZONE_GUIDE = 'KEY_ZONE_GUIDE';
  public getZoneGuide() {
    return this.getItem(this.KEY_ZONE_GUIDE);
  }
  public setZoneGuide(value: any) {
    return this.setItem(this.KEY_ZONE_GUIDE, value);
  }
  public removeZoneGuide() {
    return this.removeItem(this.KEY_ZONE_GUIDE);
  }
  public setZoneGuideNeedShow() {
    return this.setZoneGuide('guide');
  }
  public isZoneGuideNeedShow() {
    return this.getZoneGuide() === 'guide';
  }

  /**
   * IM入口引导提示只展示一次
   */
  private KEY_IM_ENTRY_GUIDE = 'KEY_IM_ENTRY_GUIDE';
  public getImEntryGuide() {
    return this.getItem(this.KEY_IM_ENTRY_GUIDE);
  }
  public setImEntryGuide(value: any) {
    return this.setItem(this.KEY_IM_ENTRY_GUIDE, value);
  }

  /**
   * 首页weekly goods引导提示只展示一次
   */
  private KEY_WEEKLY_GOODS_GUIDE = 'KEY_WEEKLY_GOODS_GUIDE';
  public getWeeklyGoodsGuide() {
    return this.getItem(this.KEY_WEEKLY_GOODS_GUIDE);
  }
  public setWeeklyGoodsGuide() {
    return this.setItem(this.KEY_WEEKLY_GOODS_GUIDE, 1);
  }
  public onceWeeklyGoodsGuide() {
    const init = this.getWeeklyGoodsGuide();
    if (!init) {
      this.setWeeklyGoodsGuide();
    }
    return init;
  }

  /**
   * ide 题目描述引导提示只展示一次
   */
  private KEY_IDE_TAB_DESC_GUIDE = 'KEY_IDE_TAB_DESC_GUIDE';
  public getIdeTabDescGuide(type: number) {
    return this.getItem(`${this.KEY_IDE_TAB_DESC_GUIDE}_${type}`);
  }
  public setIdeTabDescGuide(type: number) {
    return this.setItem(`${this.KEY_IDE_TAB_DESC_GUIDE}_${type}`, 1);
  }
  public onceIdeTabDescGuide(type: number) {
    const init = this.getIdeTabDescGuide(type);
    if (!init) {
      this.setIdeTabDescGuide(type);
    }
    return init;
  }

  /**
   * 题单的新手引导题目需要记录应该跳转的目标题目信息
   */
  private KEY_TRAINING_USER_GUIDE_NEXT_PROBLEM =
    'KEY_TRAINING_USER_GUIDE_NEXT_PROBLEM';
  public getTrainingUserGuideNextProblem() {
    return this.getItem(this.KEY_TRAINING_USER_GUIDE_NEXT_PROBLEM);
  }
  public setTrainingUserGuideNextProblem(info: any) {
    return this.setItem(this.KEY_TRAINING_USER_GUIDE_NEXT_PROBLEM, info);
  }
  public removeTrainingUserGuideNextProblem() {
    return this.removeItem(this.KEY_TRAINING_USER_GUIDE_NEXT_PROBLEM);
  }

  /**
   * 存储adtag
   */
  private KEY_ADTAG = 'KEY_ADTAG';
  public getAdtag() {
    return this.getItem(this.KEY_ADTAG);
  }
  public setAdtag(value: any) {
    return this.setItem(this.KEY_ADTAG, value);
  }
  public removeAdtag() {
    return this.removeItem(this.KEY_ADTAG);
  }

  /**
   * 存储用户登录token
   * public static readonly：供外部（如 UserService storage 监听器）直接引用 key 常量
   */
  public static readonly KEY_USER_LOGIN_TOKEN = 'KEY_USER_LOGIN_TOKEN';
  public getUserToken() {
    return this.getItem(StorageService.KEY_USER_LOGIN_TOKEN);
  }
  public setUserToken(value: any) {
    return this.setItem(StorageService.KEY_USER_LOGIN_TOKEN, value);
  }
  public removeUserToken() {
    return this.removeItem(StorageService.KEY_USER_LOGIN_TOKEN);
  }

  /**
   * 存储用户信息
   * public static readonly：供外部（如 UserService storage 监听器）直接引用 key 常量
   */
  public static readonly KEY_USER_INFO = 'KEY_USER_INFO';
  public getUserInfo() {
    return this.getItem(StorageService.KEY_USER_INFO);
  }
  public setUserInfo(value: any) {
    return this.setItem(StorageService.KEY_USER_INFO, value);
  }
  public removeUserInfo() {
    return this.removeItem(StorageService.KEY_USER_INFO);
  }

  /**
   * 存储用户等级信息
   */
  private KEY_USER_LEVEL = 'KEY_USER_LEVEL';
  public getUserLevel() {
    return this.getItem(this.KEY_USER_LEVEL);
  }
  public setUserLevel(value: any) {
    return this.setItem(this.KEY_USER_LEVEL, value);
  }
  public removeUserLevel() {
    return this.removeItem(this.KEY_USER_LEVEL);
  }

  /**
   * 存储用户默认显示的倒计时比赛
   */
  private KEY_DEFAULT_COUNTDOWN = 'KEY_DEFAULT_COUNTDOWN';
  public getDefaultCountdown() {
    return this.getItem(this.KEY_DEFAULT_COUNTDOWN);
  }
  public setDefaultCountdown(value: any) {
    return this.setItem(this.KEY_DEFAULT_COUNTDOWN, value);
  }
  public removeDefaultCountdown() {
    return this.removeItem(this.KEY_DEFAULT_COUNTDOWN);
  }

  /**
   * 存储发送验证码的倒计时
   * 解决刷新页面丢失倒计时的问题
   */
  private KEY_TCAPTURE_COUNTDOWN = 'KEY_TCAPTURE_COUNTDOWN';
  public getTCaptureCountdown() {
    const value = this.getItem(this.KEY_TCAPTURE_COUNTDOWN);
    if (value) {
      const now = Date.now();
      const gap = Math.floor((now - value) / 1000);
      if (gap > 0 && gap < 60) {
        return 60 - gap;
      }
    }
    return 0;
  }
  public setTCaptureCountdown() {
    const now = Date.now();
    return this.setItem(this.KEY_TCAPTURE_COUNTDOWN, now);
  }
  public removeTCaptureCountdown() {
    return this.removeItem(this.KEY_TCAPTURE_COUNTDOWN);
  }
  public getContestPassiveSubmited(cid: string | number) {
    const key = `${this.KEY_CONTEST_PASSIVE_SUBMITED}_${cid}`;
    return this.getItem(key);
  }
  public setContestPassiveSubmited(cid: string | number, value: any) {
    const key = `${this.KEY_CONTEST_PASSIVE_SUBMITED}_${cid}`;
    return this.setItem(key, value);
  }
  public removeContestPassiveSubmited(cid: string | number) {
    const key = `${this.KEY_CONTEST_PASSIVE_SUBMITED}_${cid}`;
    return this.removeItem(key);
  }
  private LAST_OFFLINE_MESSAGE_TIME = 'LAST_IM_OFFLINE_MESSAGE_TIME';
  public getLastOfflineMessageTime() {
    return this.getItem(this.LAST_OFFLINE_MESSAGE_TIME);
  }
  public setLastOfflineMessageTime(value: any) {
    return this.setItem(this.LAST_OFFLINE_MESSAGE_TIME, value);
  }
  public removeLastOfflineMessageTime() {
    return this.removeItem(this.LAST_OFFLINE_MESSAGE_TIME);
  }
}
